import { useState, useRef } from "react";
import { toast } from "sonner";
import { Button } from "../../ui/button.js";
import { Progress } from "../../ui/progress.js";
import { presignPhoto } from "./api.js";
import { sync } from "../../sync/SyncService.js";
import { RecipePhotoImg } from "./RecipePhotoImg.js";
import {
  validateInputFile,
  resizeImage,
  uploadPair,
  isSafari,
  ALLOWED_INPUT_TYPES,
} from "../../shared/photos.js";
import { type RecipeApiItem, MAX_PHOTOS_PER_RECIPE } from "@recipevault/shared";

type Props = {
  householdId: string;
  recipe: RecipeApiItem;
  onRecipeChange: (next: RecipeApiItem) => void;
};

type UploadPhase = "idle" | "validating" | "resizing" | "presigning" | "uploading" | "confirming";

export function PhotoUploader({ householdId, recipe, onRecipeChange }: Props) {
  const [phase, setPhase] = useState<UploadPhase>("idle");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const photoCount = (recipe.photos.coverKey ? 1 : 0) + recipe.photos.extras.length;
  const atLimit = photoCount >= MAX_PHOTOS_PER_RECIPE;
  const uploading = phase !== "idle";

  async function handleFiles(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;
    const file = files[0] as File;

    setPhase("validating");
    const validation = validateInputFile(file, { isSafari: isSafari() });
    if (!validation.ok) {
      toast.error(validation.message);
      setPhase("idle");
      return;
    }

    try {
      setPhase("resizing");
      const blobs = await resizeImage(file);

      setPhase("presigning");
      const presigned = await presignPhoto(recipe.id);

      setPhase("uploading");
      await uploadPair(blobs, { thumbUrl: presigned.thumbUrl, fullUrl: presigned.fullUrl });

      setPhase("confirming");
      const confirmed = await sync.confirmPhotoOptimistic(recipe.id, {
        photoKey: presigned.photoKey,
        version: recipe.version,
        asCover: recipe.photos.coverKey === null, // first photo auto-becomes cover
      });
      onRecipeChange(confirmed);
      toast.success("Photo added");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Photo upload failed");
    } finally {
      setPhase("idle");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleDelete(photoKey: string): Promise<void> {
    try {
      const updated = await sync.deletePhotoOptimistic(recipe.id, photoKey, recipe.version);
      onRecipeChange(updated);
      toast.success("Photo removed");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to remove photo");
    }
  }

  async function handleSetCover(photoKey: string): Promise<void> {
    try {
      const updated = await sync.confirmPhotoOptimistic(recipe.id, {
        photoKey,
        version: recipe.version,
        asCover: true,
      });
      onRecipeChange(updated);
      toast.success("Cover updated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update cover");
    }
  }

  return (
    <section aria-label="Photos" className="space-y-4">
      <h3 className="text-lg font-medium">Photos</h3>

      {recipe.photos.coverKey && (
        <div className="space-y-2">
          <span className="text-sm text-muted-foreground">Cover</span>
          <div className="relative">
            <RecipePhotoImg
              householdId={householdId}
              recipeId={recipe.id}
              photoKey={recipe.photos.coverKey}
              size="full"
              alt="Recipe cover"
              className="rounded-md max-h-64 object-cover"
            />
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="absolute top-2 right-2"
              aria-label={`Delete photo ${recipe.photos.coverKey}`}
              onClick={() => {
                void handleDelete(recipe.photos.coverKey!);
              }}
            >
              Remove
            </Button>
          </div>
        </div>
      )}

      {recipe.photos.extras.length > 0 && (
        <div className="space-y-2">
          <span className="text-sm text-muted-foreground">Additional photos</span>
          <ul className="grid grid-cols-3 gap-2">
            {recipe.photos.extras.map((photoKey) => (
              <li key={photoKey} className="relative">
                <RecipePhotoImg
                  householdId={householdId}
                  recipeId={recipe.id}
                  photoKey={photoKey}
                  size="thumb"
                  alt="Recipe photo"
                  className="rounded-md aspect-square object-cover w-full"
                />
                <div className="absolute top-1 right-1 flex gap-1">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    aria-label={`Set as cover ${photoKey}`}
                    onClick={() => {
                      void handleSetCover(photoKey);
                    }}
                  >
                    Cover
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    aria-label={`Delete photo ${photoKey}`}
                    onClick={() => {
                      void handleDelete(photoKey);
                    }}
                  >
                    ✕
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-2">
        <input
          ref={fileInputRef}
          type="file"
          id="photo-upload"
          accept={Array.from(ALLOWED_INPUT_TYPES).join(",")}
          aria-label="Choose file to upload"
          className="hidden"
          onChange={(e) => {
            void handleFiles(e.target.files);
          }}
        />
        <Button
          type="button"
          variant="outline"
          aria-label="Add photo"
          disabled={atLimit || uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {atLimit
            ? "10 photos max — remove one to add another"
            : uploading
              ? `Uploading… (${phase})`
              : "Add photo"}
        </Button>
        {uploading && <Progress value={50} aria-label="Upload progress" />}
        <p className="text-xs text-muted-foreground">
          JPEG, PNG, WebP, or HEIC. Max 25 MB. Photos are resized and stripped of metadata in your browser.
        </p>
      </div>
    </section>
  );
}
